import { useTranslation } from 'next-i18next';
import { memo, useState } from 'react';
import { BsCircleFill } from 'react-icons/bs';
import ComponentWrapper from '../../common/ContentWrapper';
import ImageWithFallback from '../../common/ImageWithFallback';
import Carousel from '../../common/Carousel';
import { PromotionType } from '../../../core/constants';
import analytics from '../../../core/analytics/analytics';
import WebSpecialNotice from '../../../../types/WebSpecialNotice';

type Props = {
  banners: WebSpecialNotice[];
};

const WebSpecialNotices = ({ banners }: Props) => {
  const { t } = useTranslation('home');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const customPaging = (i: number) => (
    <BsCircleFill
      key={i}
      size={i === currentSlideIndex ? 12 : 8}
      className={`text-micro text-black ${
        i === currentSlideIndex ? 'opacity-100' : 'opacity-50'
      } cursor-pointer`}
    />
  );

  const appendDots = (dots: JSX.Element) => (
    <div className="w-full">
      <ul className="flex items-center justify-center gap-2">{dots}</ul>
    </div>
  );

  const maxSlidesToShow = 3;
  const numOfBanners = banners.length || 0;
  const settings = {
    className: '-mx-2', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-2', // add spaces at both of the sides of cards
    infinite: numOfBanners > maxSlidesToShow,
    slidesToShow: 3,
    draggable: false,
    prevBtnStyle: '-left-3 card-carousel-arrow-btn',
    nextBtnStyle: '-right-3 card-carousel-arrow-btn',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    dots: banners.length > maxSlidesToShow,
    dotsClass: 'h-0', // kill height of dots to centralize arrow buttons
    maxSlidesToShow,
    beforeChange: (_: number, next: number) => {
      setCurrentSlideIndex(Math.ceil(next));
    },
    customPaging,
    appendDots,
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.1,
          infinite: false,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  const events = {
    onClick: (banner: WebSpecialNotice) => {
      analytics.google.ecommerce.track.promotion.view(
        {
          promotion_name: PromotionType.CAMPAIGN_BANNER,
          promotion_id: banner?.url || '',
        },
        [
          {
            item_id: banner?.url || '',
            item_name: banner?.image?.altText || '',
          },
        ]
      );
    },
  };

  return (
    <ComponentWrapper className="pt-12 sm:pt-16">
      <h2 className="app-section-title">{t('web_special_notice.title')}</h2>

      <div className="-mr-2 sm:mr-0">
        <Carousel
          settings={settings}
          items={banners}
          renderItem={(banner, prefetchImages) => (
            <ImageWithFallback
              alt={banner.image?.altText || 'Web Special Notice'}
              key={banner.id}
              className="rounded-xl"
              src={banner.image?.url || ''}
              url={banner.url}
              layout="responsive"
              width={384}
              height={240}
              fallbackSrc="/fallback-img-790x394.png"
              sizes="(max-width: 768px) 50vw, 33vw"
              onClick={() => events.onClick(banner)}
              loading={prefetchImages ? 'eager' : 'lazy'}
            />
          )}
        />
      </div>
    </ComponentWrapper>
  );
};

export default memo(WebSpecialNotices);
